import React, { useEffect } from "react";
import { useState } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Flex,
  Form,
  Image,
  Modal,
  Row,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  dateObjToDispViewDate,
  readImageFileAsync,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";
import { getCategoriesSelectListAction } from "../../../../../../redux/services/categories/categoriesServices";
import Actions from "../../../../../../redux/actions";
import useUserToken from "../../../../../../hooks/useToken";
import { appConfig } from "../../../../../../constants/config/appConfig";
import { getServiceProviderAppointmentsInfoByIdAction } from "../../../../../../redux/services/service-provider-appointments/serviceProviderAppointmentsServices";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import dayjs from "dayjs";
import { colors } from "../../../../../../constants/colors";
import { GetCardLayout } from "../../../../../common/getCardLayout";
import { routes } from "../../../../../../constants";
import { baseRouteMaker } from "../../../../../../constants/routeConsts";
import { ArrowLeftOutlined } from "@ant-design/icons";
import styles from "./AppointmentsInfo.module.scss";

const AppointmentsInfoDetails = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  const [token, setToken] = useUserToken(appConfig.SESSION_USER_TOKEN);

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  const availability = Form.useWatch("availability", { form, preserve: true });
  const category = Form.useWatch("category", { form, preserve: true });
  /* form dependencies */

  /* id dependencies */
  const { selectedAppointmentsInfoId } = useSelector(
    (state) => state.serviceProviderAppointments
  );
  /* id dependencies */

  /* detail dependencies */
  const [appointmentsInfoDetails, setAppointmentsInfoDetails] = useState(null);
  /* detail dependencies */

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const [servicesAttachmentList, setServicesAttachmentList] = useState([]);
  /* module dependencies for attachment */

  /* function definition for select list API calls */
  const selectListApiCalls = (details) => {
    // Categories select list
    dispatch(getCategoriesSelectListAction(setSelectListLoader)).then(
      (apiRes) => {
        const categories = apiRes?.data;
        details &&
          categories &&
          categories?.forEach((element) => {
            if (element._id === details?.categoryInfo?.category) {
              dispatch(
                Actions.createAction(Actions.GET_SUB_CATEGORIES_SELECT_LIST, {
                  subCategoriesSelectList: element?.subcategories,
                })
              );
            }
          });
      }
    );
  };
  /* function definition for select list API calls */

  useEffect(() => {
    /* initial dependencies - Edit */
    if (
      selectedAppointmentsInfoId &&
      selectedAppointmentsInfoId !== "" &&
      selectedAppointmentsInfoId !== null &&
      selectedAppointmentsInfoId !== undefined
    ) {
      const dataToBeSent = {
        selectedAppointmentsInfoId,
      };
      dispatch(
        getServiceProviderAppointmentsInfoByIdAction(dataToBeSent, setLoading)
      ).then((apiRes) => {
        const response = apiRes;
        setAppointmentsInfoDetails(response);
        setAttachmentList(
          response?.documents?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
        setServicesAttachmentList(
          response?.service_id?.images?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
      });
    } else {
      /* initial dependencies - Edit */
      selectListApiCalls(null);
    }
  }, []);

  /* open image preview for dialog */
  const handleAttachmentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await readImageFileAsync(file.originFileObj);
    }
    setPreviewImageSrc(file?.url || file?.preview);
    setOpenPreviewDialog(true);
    const tempPreviewTitle =
      file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
    setPreviewTitle(tempPreviewTitle);
  };
  /* open image preview for dialog */

  return (
    <div className={styles.appointmentsInfoDetailsContainer}>
      <Flex vertical gap="2rem">
        <GetCardLayout>
          <Flex vertical gap="2rem">
            <Flex gap="1rem">
              <Button
                type="default"
                shape="circle"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={() =>
                  navigate(baseRouteMaker(routes.APPOINTMENTS_INFO))
                }
              />
              <Typography.Title underline level={2}>
                Appointment Info
              </Typography.Title>
            </Flex>
            <Row gutter={[16, 16]}>
              <Col span={8}>
                <RequiredLabel text="Profile Image" required={false} />
                {appointmentsInfoDetails?.user_id?.profile_image ? (
                  <Image
                    style={{
                      width: "120px",
                      maxWidth: "100%",
                      border: "1px solid lightGray",
                      padding: "5px",
                      borderRadius: "10px",
                    }}
                    src={appointmentsInfoDetails?.user_id?.profile_image ?? "-"}
                    alt="Profile Image"
                  />
                ) : (
                  <>-</>
                )}
              </Col>
              <Col span={8}>
                <RequiredLabel text="Customer Name" required={false} />
                <div>
                  {appointmentsInfoDetails?.user_id
                    ? `${appointmentsInfoDetails?.user_id?.first_name} ${appointmentsInfoDetails?.user_id?.last_name}`
                    : "-"}
                </div>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Customer Email" required={false} />
                <div>
                  {appointmentsInfoDetails?.user_id
                    ? appointmentsInfoDetails?.user_id?.email
                    : "-"}
                </div>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Appointment Time" required={false} />
                <div>
                  {appointmentsInfoDetails?.appointment_date_time ? timeObjToDispViewTimeWithSuffix(dayjs(appointmentsInfoDetails?.appointment_date_time).utc().local().format('ddd, MMM D, H:mm z')) : "-"}
                </div>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Appointment Date" required={false} />
                <div>
                  {dateObjToDispViewDate(
                    appointmentsInfoDetails?.appointment_date,
                    "ddmmyyyy"
                  )}
                </div>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Booking For" required={false} />
                <div>{appointmentsInfoDetails?.booking_for}</div>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Charges" required={false} />
                <div>{appointmentsInfoDetails?.total_amount?.toFixed(2)}</div>
              </Col>

              <Col span={8}>
                <RequiredLabel text="Payment Status" required={false} />
                <Tag
                  bordered={false}
                  style={{
                    background: appointmentsInfoDetails?.is_payment_completed
                      ? colors.lightTeal
                      : colors.lightPinkishRed,
                    color: appointmentsInfoDetails?.is_payment_completed
                      ? colors.darkTeal
                      : colors.darkMaroon,
                  }}
                >
                  {appointmentsInfoDetails?.is_payment_completed
                    ? t("Modules.Appointments.paid")
                    : t("Modules.Appointments.due")}
                </Tag>
              </Col>
              <Col span={8}>
                <RequiredLabel text="Order Number" required={false} />
                <div>{appointmentsInfoDetails?.order_number}</div>
              </Col>

              <Col span={8}>
                <RequiredLabel text="National ID Documents" required={false} />
                {attachmentList?.length !== 0 ? (
                  <Flex vertical align="center" justify="center">
                    <Upload
                      listType="picture-card"
                      fileList={attachmentList.filter(
                        (element) => element.url !== ""
                      )}
                      accept="image/*"
                      onPreview={handleAttachmentPreview}
                      className="uploadContainer"
                    ></Upload>
                    <Modal
                      open={openPreviewDialog}
                      title={previewTitle}
                      footer={null}
                      onCancel={() => setOpenPreviewDialog(false)}
                    >
                      <img style={{ width: "100%" }} src={previewImageSrc} />
                    </Modal>
                  </Flex>
                ) : (
                  <>-</>
                )}
              </Col>

              {appointmentsInfoDetails?.booking_for === "other" && (
                <Col span={24}>
                  <Flex vertical gap="1rem">
                    <Typography.Title level={3}>
                      Booking For Details :
                    </Typography.Title>
                    <Row gutter={[16, 16]}>
                      <Col span={8}>
                        <RequiredLabel text="Name" required={false} />
                        <div>{`${appointmentsInfoDetails?.booking_for_details?.first_name} ${appointmentsInfoDetails?.booking_for_details?.last_name}`}</div>
                      </Col>
                      <Col span={8}>
                        <RequiredLabel text="Email" required={false} />
                        <div>
                          {appointmentsInfoDetails?.booking_for_details?.email}
                        </div>
                      </Col>
                      <Col span={8}>
                        <RequiredLabel text="Relationship" required={false} />
                        <div>
                          {
                            appointmentsInfoDetails?.booking_for_details
                              ?.relationship
                          }
                        </div>
                      </Col>
                    </Row>
                  </Flex>
                </Col>
              )}
            </Row>
          </Flex>
        </GetCardLayout>

        {appointmentsInfoDetails?.service_id &&
          appointmentsInfoDetails?.service_id !== null &&
          appointmentsInfoDetails?.service_id !== undefined && (
            <GetCardLayout>
              <Flex vertical gap="2rem">
                <Typography.Title underline level={2}>
                  Service Info
                </Typography.Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <RequiredLabel text="Documents" required={false} />
                    {servicesAttachmentList?.length !== 0 ? (
                      <Flex vertical align="center" justify="center">
                        <Upload
                          listType="picture-card"
                          fileList={servicesAttachmentList}
                          accept="image/*"
                          onPreview={handleAttachmentPreview}
                          className="uploadContainer"
                        ></Upload>
                        <Modal
                          open={openPreviewDialog}
                          title={previewTitle}
                          footer={null}
                          onCancel={() => setOpenPreviewDialog(false)}
                        >
                          <img
                            style={{ width: "100%" }}
                            src={previewImageSrc}
                          />
                        </Modal>
                      </Flex>
                    ) : (
                      <>-</>
                    )}
                  </Col>

                  <Col span={8}>
                    <RequiredLabel text="Service" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.title
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Category" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.categoryInfo
                            ?.categoryName
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Sub Category" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.categoryInfo
                            ?.subcategory
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Price" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.price
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Availability" required={false} />
                    <div>
                      <ol>
                        {appointmentsInfoDetails?.service_id &&
                          appointmentsInfoDetails?.service_id?.availability?.map(
                            (element) => {
                              return (
                                <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                              );
                            }
                          )}
                      </ol>
                    </div>
                  </Col>
                </Row>
              </Flex>
            </GetCardLayout>
          )}
          
        {appointmentsInfoDetails?.service_provider_id &&
          appointmentsInfoDetails?.service_provider_id !== null &&
          appointmentsInfoDetails?.service_provider_id !== undefined && (
            <GetCardLayout>
              <Flex vertical gap="2rem">
                <Typography.Title underline level={2}>
                  Staff Info
                </Typography.Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <RequiredLabel text="Profile Image" required={false} />
                  {appointmentsInfoDetails?.service_provider_id?.profile_image ? (
                    <Image
                      style={{
                        width: "120px",
                        maxWidth: "100%",
                        border: "1px solid lightGray",
                        padding: "5px",
                        borderRadius: "10px",
                      }}
                      src={appointmentsInfoDetails?.service_provider_id?.profile_image ?? "-"}
                      alt="Profile Image"
                    />
                  ) : (
                    <>-</>
                  )}
                  </Col>

                  <Col span={8}>
                    <RequiredLabel text="Staff Name" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `${appointmentsInfoDetails?.service_provider_id?.first_name} ${appointmentsInfoDetails?.service_provider_id?.last_name}`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Email" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? appointmentsInfoDetails?.service_provider_id?.email
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Phone" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `+${appointmentsInfoDetails?.service_provider_id?.phone?.country_code}-${appointmentsInfoDetails?.service_provider_id?.phone?.phone_number}`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Fees" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? appointmentsInfoDetails?.service_provider_id?.fees
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Experience" required={false} />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `${appointmentsInfoDetails?.service_provider_id?.experience} years`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text="Availability" required={false} />
                    <div>
                      <ol>
                        {appointmentsInfoDetails?.service_provider_id &&
                          appointmentsInfoDetails?.service_provider_id?.availability?.map(
                            (element) => {
                              return (
                                <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                              );
                            }
                          )}
                      </ol>
                    </div>
                  </Col>
                </Row>
              </Flex>
            </GetCardLayout>
          )}
      </Flex>
    </div>
  );
};

export default AppointmentsInfoDetails;
